import React, { useState, useRef } from 'react';
import { DeepDiveModal } from './LLMTop10';
import CIDTooltip from './CIDTooltip';
import PRODUCT_DB_MAP from '../config/productDatabaseMap';
import { API_BASE } from '../config/apiConfig';

/**
 * CustomIntelligencePanel — Manages custom BI questions, LLM suggestions,
 * search, edit, instructions (SQL/NLP), and summarized reports.
 */

// LLM top-10 questions by industry — base set (Gemini default)
// Extended pool: 20 questions per industry so we can backfill when questions are adopted
const LLM_QUESTIONS_BASE = {
  restaurant: [
    'What is our food cost percentage by menu category this month?',
    'Which menu items have the highest profit margin after ingredient costs?',
    'What is our table turnover rate by day of week?',
    'How does our average check size compare across lunch vs dinner?',
    'What is our labor cost as a percentage of revenue this week?',
    'Which order channel has the highest customer retention rate?',
    'What are our peak hours and how does staffing align with demand?',
    'What is our waste percentage by food category?',
    'How many repeat customers visited in the last 30 days vs new customers?',
    'What is our delivery time performance by third-party partner?',
    // Extended pool (backfill candidates)
    'What is our average table wait time during peak hours?',
    'Which menu categories have declining sales over the last 3 months?',
    'What is our tip percentage trend by server?',
    'How does our catering revenue compare month over month?',
    'What is our ingredient spoilage rate by supplier?',
    'Which promotions drove the highest incremental revenue?',
    'What is our kitchen ticket time by order type?',
    'How does weather affect our daily order volume?',
    'What is our gift card redemption rate?',
    'Which day of the week has the lowest revenue and why?',
    'What is our average order value by order channel?',
    'Which ingredient prices have increased the most this quarter?',
    'What is our reservation no-show rate by day?',
    'How does our dine-in vs takeaway revenue split trend weekly?',
    'What is our food cost per plate for our top 10 items?',
    'Which delivery partners have the highest complaint rate?',
    'What is our average prep time by menu category?',
    'How many catering inquiries converted to bookings this month?',
    'What is our beverage-to-food revenue ratio?',
    'Which staff members have the highest upsell rate?',
  ],
  lawn: [
    'What is our revenue per crew member per day?',
    'Which service zones have the highest customer density?',
    'What is our equipment utilization rate by type?',
    'How does weather impact our weekly job completion rate?',
    'What is our customer acquisition cost by marketing channel?',
    'Which seasonal services have the highest upsell conversion?',
    'What is our average response time from inquiry to first service?',
    'How does crew size affect job completion time?',
    'What is our fuel cost per job by zone?',
    'Which customers are at risk of churning based on service frequency decline?',
  ],
  hvac: [
    'What is our first-time fix rate by technician?',
    'Which equipment brands generate the most repeat service calls?',
    'What is our average time from dispatch to arrival by zone?',
    'How does seasonal demand affect our parts inventory turnover?',
    'What is our warranty claim rate by installation year?',
    'Which service types have the highest customer satisfaction scores?',
    'What is our revenue per technician per week?',
    'How many emergency calls convert to maintenance contracts?',
    'What is our average job profitability after parts and labor?',
    'Which zip codes have the highest service call density?',
  ],
  insurance: [
    'What is our claims-to-premium ratio by policy type?',
    'Which agents have the highest cross-sell rate?',
    'What is our average time from claim filing to settlement?',
    'How does our loss ratio trend quarter over quarter?',
    'Which policy types have the highest lapse rate?',
    'What is our customer lifetime value by acquisition channel?',
    'How many policies are within 60 days of renewal?',
    'What is our underwriting approval rate by risk score band?',
    'Which offices have the highest new policy growth rate?',
    'What is our average premium per policy by coverage type?',
  ],
  plumbing: [
    'What is our average time-to-dispatch by job priority?',
    'Which job types have the highest callback rate?',
    'What is our parts markup percentage by category?',
    'How does franchise vs retail subscriber revenue trend monthly?',
    'What is our lead conversion rate by source?',
    'Which technicians have the fastest average job completion time?',
    'What is our customer satisfaction score by location type?',
    'How many jobs require follow-up visits within 30 days?',
    'What is our average invoice amount by job complexity?',
    'Which areas have unmet demand based on declined jobs?',
  ],
  manufacturing: [
    'Which manufacturing assets currently have failure probability above 0.75 and what is their estimated Remaining Useful Life?',
    'What is our current MTBF and MTTR performance versus CEO directive targets (99.5% availability, <3hr MTTR)?',
    'What is the estimated annual unplanned downtime cost and which high-criticality assets are driving it?',
    'What is our predictive-to-reactive maintenance ratio across all production lines this quarter?',
    'How many refrigeration temperature excursion events have occurred in the last 30 days and are any at FDA compliance risk?',
    'Which production lines have maintenance backlogs exceeding the LOB threshold of 5 open tasks?',
    'What is our LOTO compliance rate and are there any outstanding OSHA 29 CFR 1910.147 gaps?',
    'How does our energy consumption per production line compare to the CEO Directive 007 target of 10% reduction?',
    'Which bearing, compressor, or belt failure patterns are trending upward based on sensor telemetry?',
    'What is our FDA inspection readiness score based on LOTO records, maintenance documentation, and food safety controls?',
    'How many P1-Critical work orders were dispatched vs pending approval in the last 7 days?',
    'Which assets show multi-sensor correlation patterns (vibration + temperature) indicating accelerated degradation?',
    'What is the average work order approval cycle time and are any escalation SLAs being breached?',
    'What is our PM (Preventive Maintenance) compliance rate versus the LOB target of 95%?',
    'Which food-contact equipment maintenance activities are missing post-maintenance sanitization verification?',
    'What is the total AI model cost per prediction-to-dispatch pipeline and are we within the $0.20 budget?',
    'How does production schedule adherence compare to the CEO Directive 006 target of 98%?',
    'Which assets have the highest criticality scores but the lowest recent maintenance frequency?',
    'What sensor types are generating the most anomaly alerts and what are the dominant failure modes predicted?',
    'How many emergency maintenance interventions (P1-Critical) occurred this month versus the 30% reduction target?',
  ],
  predictivemaintenance: [
    'Which manufacturing assets currently have failure probability above 0.75 and what is their estimated Remaining Useful Life?',
    'What is our current MTBF and MTTR performance versus CEO directive targets (99.5% availability, <3hr MTTR)?',
    'What is the estimated annual unplanned downtime cost and which high-criticality assets are driving it?',
    'What is our predictive-to-reactive maintenance ratio across all production lines this quarter?',
    'How many refrigeration temperature excursion events have occurred in the last 30 days and are any at FDA compliance risk?',
    'Which production lines have maintenance backlogs exceeding the LOB threshold of 5 open tasks?',
    'What is our LOTO compliance rate and are there any outstanding OSHA 29 CFR 1910.147 gaps?',
    'How does our energy consumption per production line compare to the CEO Directive 007 target of 10% reduction?',
    'Which bearing, compressor, or belt failure patterns are trending upward based on sensor telemetry?',
    'What is our FDA inspection readiness score based on LOTO records, maintenance documentation, and food safety controls?',
    'How many P1-Critical work orders were dispatched vs pending approval in the last 7 days?',
    'Which assets show multi-sensor correlation patterns (vibration + temperature) indicating accelerated degradation?',
    'What is the average work order approval cycle time and are any escalation SLAs being breached?',
    'What is our PM (Preventive Maintenance) compliance rate versus the LOB target of 95%?',
    'Which food-contact equipment maintenance activities are missing post-maintenance sanitization verification?',
    'What is the total AI model cost per prediction-to-dispatch pipeline and are we within the $0.20 budget?',
    'How does production schedule adherence compare to the CEO Directive 006 target of 98%?',
    'Which assets have the highest criticality scores but the lowest recent maintenance frequency?',
    'What sensor types are generating the most anomaly alerts and what are the dominant failure modes predicted?',
    'How many emergency maintenance interventions (P1-Critical) occurred this month versus the 30% reduction target?',
  ],
  general: [
    'What is our month-over-month revenue growth rate?',
    'Which products or services generate the highest margin?',
    'What is our customer retention rate over the last 90 days?',
    'How does our staffing level correlate with revenue per day?',
    'What are our top 5 expense categories this month?',
    'What is our average transaction value trend over 6 months?',
    'Which customer segments contribute the most revenue?',
    'What is our accounts receivable aging summary?',
    'How does our current month performance compare to same month last year?',
    'What are the leading indicators of customer churn in our data?',
  ],
  medical: [
    'What is our patient no-show rate by day of week?',
    'Which treatments have the highest patient satisfaction scores?',
    'What is our average revenue per patient visit by insurance type?',
    'How does treatment room utilization vary by time of day?',
    'What is our medical supply waste percentage by category?',
    'Which providers generate the most revenue per hour?',
    'What is our insurance claim denial rate by payer?',
    'How many patients are overdue for follow-up appointments?',
    'What is our patient acquisition cost by referral source?',
    'Which treatment packages have the highest upsell conversion?',
  ],
  valet: [
    'What is our average vehicle dwell time by day of week?',
    'How does mall event traffic impact valet demand?',
    'What is our tip revenue per valet per shift?',
    'Which EV charging stations have the longest queue times?',
    'What is our vehicle damage incident rate per 1000 tickets?',
    'How does weather affect valet volume?',
    'What is our express detail conversion rate from valet customers?',
    'Which parking zones have the fastest retrieval times?',
    'What is our revenue per parking spot per day?',
    'How many repeat valet customers do we have this month?',
  ],
  logistics: [
    'What is our cost per delivery by service type?',
    'Which routes have the highest fuel cost variance?',
    'What is our warehouse capacity utilization by zone?',
    'How does driver experience correlate with delivery speed?',
    'What is our return-to-origin rate and top reasons?',
    'Which customers generate the most shipment volume?',
    'What is our temperature excursion rate for cold chain?',
    'How does time of day affect delivery success rate?',
    'What is our fleet downtime percentage by vehicle age?',
    'Which contractor agencies provide the best on-time performance?',
  ],
  realty: [
    'What is our average list-to-sale price ratio by neighborhood?',
    'Which property types have the fastest time to close?',
    'What is our rental vacancy cost per month?',
    'How does staging affect days on market?',
    'What is our client acquisition cost by lead source?',
    'Which agents have the highest client retention rate?',
    'What is our average commission per transaction by price band?',
    'How many showings does it take to get an offer by price range?',
    'What is our lease renewal rate by property type?',
    'Which neighborhoods have the highest rental yield?',
  ],
  banking: [
    'What is our current loan-to-deposit ratio?',
    'Show the balance sheet summary as of today',
    'What is our net interest margin trend over the last 6 months?',
    'Break down total assets by type — loans, securities, cash',
    'Show deposit stability scores — core vs non-core deposits',
    'What is our total risk-weighted assets?',
    'Compare base vs stress scenario cashflows for next quarter',
    'What is our Tier 1 capital ratio vs regulatory minimum?',
    'Show loan prepayment behavior — CPR and PSA speed trends',
    'What is our liquidity coverage ratio?',
    'Which product categories have the highest default probability?',
    'What is our cost of funds trend by liability type?',
    'Show the maturity gap analysis for the next 12 months',
    'What is our exposure at default by customer segment?',
    'How does our capital adequacy compare to peer banks?',
    'What is our interest rate sensitivity under a 200bp shock?',
    'Show deposit runoff projections under stress scenario',
    'What is our loan concentration by industry sector?',
    'Which accounts have the highest loss given default?',
    'What is our net interest income forecast for next quarter?',
  ],
  grocery: [
    'What are today\'s total sales across both stores?',
    'Show top 10 selling products this week by revenue',
    'Which products are below reorder level?',
    'Compare retail vs dine-in vs takeaway revenue this month',
    'What is our most popular menu item by order count?',
    'Show kitchen order status breakdown — pending, preparing, ready, served',
    'Which suppliers have pending deliveries?',
    'Show customer loyalty points distribution',
    'What is our peak sales hour across all stores?',
    'Compare store performance — Main vs Express',
    'What is our average basket size by order type?',
    'Which product categories have the highest margin?',
    'Show inventory turnover rate by category',
    'What is our payment method breakdown — UPI, card, cash?',
    'Which menu items have the longest prep time?',
    'What is our daily sales trend for the last 30 days?',
    'Show purchase order status — delivered, in-transit, pending',
    'Which perishable products have the highest waste rate?',
    'What is our customer visit frequency distribution?',
    'Compare weekday vs weekend sales patterns',
  ],
  geopolitical: [
    'What are today\'s China-US geopolitical events with severity scores and 4-pillar classification?',
    'Which companies have the highest exposure to this week\'s events? Show top 10 with impact scores.',
    'Show the 14-day severity trend — is tension escalating or de-escalating?',
    'What are the current Bull/Base/Bear scenario probabilities for the Nov 2026 tariff deadline?',
    'Which GICS sectors are most impacted by recent export control events?',
    'Show US-initiated vs China-initiated event comparison for the past 30 days.',
    'What is the corporate revenue-at-risk ranking for the top 10 exposed companies?',
    'How many days until the Nov 10, 2026 tariff snap-back deadline and what are the scenarios?',
    'Which events this week were classified as Critical severity? Show originator and sector impact.',
    'Show sector weight distribution across all 11 GICS sectors with current impact scores.',
    // Extended pool (backfill candidates)
    'What is the 7-day moving average severity score and how does it compare to the 30-day baseline?',
    'Which semiconductor companies face the highest risk from new export control announcements?',
    'Show the event classification breakdown: Economy vs Social vs Cultural vs Entertainment.',
    'What portfolio protection actions should I consider based on this week\'s escalation pattern?',
    'Which supply chain corridors are most disrupted by current trade policy changes?',
    'Show the historical pattern comparison — how does current tension compare to 2018 trade war levels?',
    'What are the top 5 corporate earnings calls that mentioned China risk this quarter?',
    'Which bilateral diplomatic events signal potential de-escalation opportunities?',
    'Show the tariff impact forecast by sector for Q3 2026.',
    'What is the confidence tier distribution of this week\'s events (Confirmed vs Developing vs Speculative)?',
  ],
  procurement: [
    'Which tenders this fiscal year have a bid submission window shorter than the statutory minimum?',
    'Show top 10 vendors by award concentration across PWD contracts in the last 90 days.',
    'Compare estimated vs awarded contract values for road works in Coimbatore district this quarter.',
    'Which tenders had identical bid pricing across multiple vendors?',
    'Show single-bid tender rate by department for the current fiscal year.',
    'Which contracts have a post-award variation greater than 15% without a recorded approval?',
    'Show pending approvals older than SLA by officer and district.',
    'Which payments were released before milestone acceptance documentation was present?',
    'Show MSME participation rate by department and district.',
    'Which AI risk cases are open pending review this week, grouped by severity?',
    // Extended pool (backfill candidates)
    'Show budget utilization versus allocation by department for FY current.',
    'Which vendor documents expire within the next 30 days?',
    'Show cycle-time delay by stage: publication → evaluation → award → contract → payment.',
    'Which tenders are flagged with restricted specifications matching a single vendor?',
    'Show compliance violations by policy category and district.',
    'Which emergency procurements have been used more than 3 times in 180 days for a predictable category?',
    'Show CM directive alignment scorecard by district.',
    'Which appeals have exceeded SLA without a recorded appellate order?',
    'Show audit trail completeness rate by tender for the current fiscal year.',
    'Which bids show price variance greater than 25% versus the district-category benchmark?',
  ],
};

// Per-provider question overrides — each LLM generates different analytical angles
const LLM_PROVIDER_QUESTIONS = {
  gemini: {}, // uses base questions as-is
  chatgpt: {
    general: [
      'What anomalies exist in our revenue data over the past 60 days?',
      'Which operational bottlenecks are reducing our throughput the most?',
      'What is the predicted revenue for next month based on current trends?',
      'How do our unit economics compare across product lines?',
      'What customer cohorts show the strongest lifetime value growth?',
      'Which cost centers have exceeded budget by more than 10% this quarter?',
      'What is our net promoter score trend and what drives detractors?',
      'How does employee productivity vary by shift and department?',
      'What pricing changes would maximize margin without losing volume?',
      'Which marketing channels have the best ROI this quarter?',
    ],
    restaurant: [
      'What menu items should we discontinue based on low margin and low volume?',
      'Predict our staffing needs for next week based on reservation trends.',
      'Which ingredient prices have increased the most and how does it affect menu profitability?',
      'What is the optimal menu price for our top 5 items to maximize revenue?',
      'How does table seating arrangement affect average spend per guest?',
      'What promotions drove the highest incremental revenue last month?',
      'Which delivery partners have the highest complaint rate per order?',
      'What is our food waste cost and which prep stations contribute most?',
      'How does weather forecast correlate with expected dine-in vs delivery split?',
      'What upsell combinations have the highest attach rate?',
    ],
    medical: [
      'Which patients are at highest risk of missing their next appointment?',
      'What treatment pathways generate the best patient outcomes per dollar spent?',
      'How does provider scheduling efficiency compare across the practice?',
      'What is our revenue leakage from coding errors and missed charges?',
      'Which insurance payers have the slowest reimbursement cycle?',
      'What patient demographics show the highest growth in new visits?',
      'How does our no-show rate compare to industry benchmarks?',
      'What preventive care services are underutilized by our patient base?',
      'Which referral sources produce the highest-value patients?',
      'What is the projected impact of adding evening hours on revenue?',
    ],
    insurance: [
      'Which policy segments have the highest probability of lapsing next quarter?',
      'What is the predicted claims cost for our current book of business?',
      'How does agent tenure correlate with policy retention rates?',
      'Which underwriting rules are causing the most false declines?',
      'What cross-sell opportunities exist in our current customer base?',
      'How does our pricing compare to competitors by coverage type and region?',
      'What fraud indicators should we monitor based on recent claims patterns?',
      'Which marketing campaigns produced the lowest cost-per-acquisition?',
      'What is the optimal renewal discount to maximize retention vs margin?',
      'How does customer satisfaction score predict renewal probability?',
    ],
    realty: [
      'Which listings are overpriced based on comparable sales data?',
      'What is the predicted time-to-close for our current active listings?',
      'How does virtual staging ROI compare to physical staging?',
      'Which neighborhoods are trending up in buyer demand this quarter?',
      'What agent activities correlate most strongly with closing success?',
      'How does our commission structure compare to market rates?',
      'What is the optimal listing price range for maximum showing volume?',
      'Which lead nurturing sequences have the highest conversion rate?',
      'What seasonal patterns should inform our marketing calendar?',
      'How does open house attendance predict offer probability?',
    ],
    geopolitical: [
      'What is the predicted escalation probability for the next 30 days based on current event patterns?',
      'Which companies should hedge their China supply chain exposure based on this week\'s policy signals?',
      'How does the current tariff trajectory compare to the 2018-2019 trade war escalation curve?',
      'What sector rotation strategy would optimize portfolio protection given current geopolitical signals?',
      'Which export control announcements have the highest probability of expanding to new product categories?',
      'What is the predicted impact on semiconductor supply chains if the Bear scenario materializes?',
      'Which bilateral diplomatic channels show the strongest de-escalation signals?',
      'How should corporate boards adjust their China strategy based on the last 90 days of events?',
      'What is the optimal timing for supply chain diversification decisions given current policy momentum?',
      'Which companies have the most concentrated revenue exposure to potential retaliatory tariffs?',
    ],
    procurement: [
      'Which procurement patterns suggest potential collusion based on statistical bid analysis?',
      'What process improvements would reduce the average tender-to-award cycle time?',
      'Which departments have the highest variance between estimated and actual contract costs?',
      'What vendor diversification strategies would reduce single-source dependency risk?',
      'Which policy compliance gaps pose the highest audit risk this fiscal year?',
      'How does our MSME participation rate compare to statutory requirements by category?',
      'What workflow bottlenecks are causing the most SLA breaches in approval chains?',
      'Which emergency procurement patterns suggest predictable needs that should be planned?',
      'What transparency improvements would most reduce public grievance filings?',
      'How does our district-level budget utilization correlate with development outcomes?',
    ],
  },
  claude: {
    general: [
      'What are the ethical considerations in our current pricing strategy?',
      'How can we improve data quality across our reporting pipeline?',
      'What process inefficiencies exist between departments based on workflow data?',
      'How does our customer communication frequency affect satisfaction scores?',
      'What compliance risks exist in our current operational data?',
      'Which business processes would benefit most from automation?',
      'How transparent is our reporting to stakeholders and where are the gaps?',
      'What data governance improvements would increase our analytical accuracy?',
      'How does our employee satisfaction data correlate with customer outcomes?',
      'What are the unintended consequences of our current incentive structures?',
    ],
    restaurant: [
      'How does our allergen management process compare to FDA best practices?',
      'What food safety compliance gaps exist in our current temperature logs?',
      'How can we reduce food waste while maintaining menu quality and variety?',
      'What accessibility improvements would our dining experience benefit from?',
      'How does our tip distribution model affect staff retention and fairness?',
      'What nutritional transparency improvements would our customers value?',
      'How does our supply chain resilience compare across ingredient categories?',
      'What training gaps exist based on customer complaint patterns?',
      'How can we improve our sustainability metrics without increasing costs?',
      'What community engagement opportunities align with our brand values?',
    ],
    medical: [
      'What patient consent process improvements would reduce compliance risk?',
      'How does our HIPAA compliance posture compare across data touchpoints?',
      'What health equity gaps exist in our patient access patterns?',
      'How can we improve informed consent documentation for complex procedures?',
      'What clinical workflow inefficiencies increase patient wait times?',
      'How does our patient communication approach affect treatment adherence?',
      'What data privacy improvements should we prioritize for patient records?',
      'How can we better support patients with limited health literacy?',
      'What quality metrics should we track beyond standard clinical outcomes?',
      'How does our care coordination process affect patient experience scores?',
    ],
    geopolitical: [
      'What ethical frameworks should guide our response to geopolitical intelligence signals?',
      'How can we improve data provenance and source verification for event classification?',
      'What transparency gaps exist in our severity scoring methodology?',
      'How does our event classification accuracy compare across the four pillars?',
      'What bias risks exist in our corporate impact scoring model?',
      'How can we better communicate uncertainty in our forecast scenarios?',
      'What governance improvements would increase subscriber trust in our intelligence?',
      'How does our event coverage balance across US-initiated vs China-initiated actions?',
      'What quality controls should we add to prevent false escalation signals?',
      'How can we improve the actionability of our intelligence without overstepping advisory boundaries?',
    ],
    procurement: [
      'What ethical safeguards should we add to our AI risk detection to prevent false accusations?',
      'How can we improve transparency in our automated vendor scoring methodology?',
      'What due process improvements would strengthen our risk case review workflow?',
      'How does our system handle edge cases where policy rules conflict with practical needs?',
      'What data quality improvements would increase the accuracy of our anomaly detection?',
      'How can we better protect vendor privacy while maintaining procurement transparency?',
      'What governance frameworks should guide our AI-assisted decision recommendations?',
      'How does our system ensure equitable treatment across vendor sizes and categories?',
      'What audit trail improvements would strengthen our compliance posture?',
      'How can we better balance efficiency with thoroughness in our approval workflows?',
    ],
  },
  bedrock: {
    general: [
      'What AWS cost optimization opportunities exist in our infrastructure?',
      'How does our API latency distribution affect user experience metrics?',
      'What data pipeline bottlenecks are increasing our processing time?',
      'How can we improve our disaster recovery readiness based on current architecture?',
      'What security vulnerabilities exist in our current access patterns?',
      'How does our database query performance trend over the last 30 days?',
      'What auto-scaling improvements would reduce our compute costs?',
      'How does our data retention policy align with compliance requirements?',
      'What observability gaps exist in our current monitoring setup?',
      'Which microservices have the highest error rates and what are the root causes?',
    ],
    restaurant: [
      'How does our POS system latency affect order throughput during peak hours?',
      'What IoT sensor data patterns predict equipment failures before they happen?',
      'How can we optimize our kitchen display system routing for faster ticket times?',
      'What real-time inventory tracking improvements would reduce stockouts?',
      'How does our online ordering platform performance compare across devices?',
      'What data integration gaps exist between our POS, inventory, and accounting systems?',
      'How can we use historical order data to optimize prep schedules?',
      'What automated alerting rules would catch food safety issues faster?',
      'How does our reservation system capacity planning align with actual demand?',
      'What API performance improvements would speed up third-party delivery integration?',
    ],
    geopolitical: [
      'How does our event ingestion pipeline latency affect real-time severity scoring?',
      'What data quality metrics should we track for our geopolitical event classification model?',
      'How can we optimize our sector scoring algorithm for faster corporate impact calculation?',
      'What observability gaps exist in our CID-DDLS personalization pipeline?',
      'How does our forecast model accuracy degrade over different time horizons?',
      'What caching strategies would improve subscriber dashboard load times during high-event days?',
      'How can we improve our NLP entity extraction accuracy for company name recognition?',
      'What automated alerting would catch event classification errors before subscriber delivery?',
      'How does our RAG retrieval accuracy compare across different policy document types?',
      'What infrastructure scaling patterns correlate with geopolitical event spikes?',
    ],
    procurement: [
      'How does our anomaly detection model performance vary across different tender categories?',
      'What data pipeline improvements would reduce our risk scoring latency?',
      'How can we optimize our document processing for faster tender intake?',
      'What observability gaps exist in our approval workflow automation?',
      'How does our bid analysis algorithm accuracy compare across different procurement types?',
      'What caching strategies would improve district dashboard load times?',
      'How can we improve our OCR accuracy for scanned tender documents?',
      'What automated alerting would catch SLA breaches before they become critical?',
      'How does our vendor scoring model performance degrade with sparse data?',
      'What infrastructure scaling patterns correlate with fiscal year-end procurement spikes?',
    ],
  },
};

// Resolve questions for a given provider + industry
function getQuestionsForProvider(providerId, industryKey) {
  const base = LLM_QUESTIONS_BASE[industryKey] || LLM_QUESTIONS_BASE.general;
  const providerOverrides = LLM_PROVIDER_QUESTIONS[providerId] || {};
  const providerQuestions = providerOverrides[industryKey] || [];
  // Merge: provider-specific questions first, then base pool (deduplicated)
  if (providerQuestions.length > 0) {
    const seen = new Set(providerQuestions.map(q => q.toLowerCase().trim()));
    const extra = base.filter(q => !seen.has(q.toLowerCase().trim()));
    return [...providerQuestions, ...extra];
  }
  return base;
}

const LLM_PROVIDERS = [
  { id: 'gemini', label: 'Gemini (Google)', icon: '🔷' },
  { id: 'chatgpt', label: 'ChatGPT (OpenAI)', icon: '🟢' },
  { id: 'claude', label: 'Claude (Anthropic)', icon: '🟣' },
  { id: 'bedrock', label: 'Bedrock (AWS)', icon: '🟠' },
];

// Atomic Intelligence — four lifecycle topics with industry-specific examples
const ATOMIC_TOPICS = {
  recommend: {
    label: 'Recommend',
    icon: '💡',
    purpose: 'AI suggests an action based on data patterns',
    examples: {
      restaurant: 'Which menu items have the highest profit margin after ingredient costs?',
      lawn: 'Which service zones have the highest customer density?',
      hvac: 'Which equipment brands generate the most repeat service calls?',
      insurance: 'Which agents have the highest cross-sell rate?',
      plumbing: 'Which job types have the highest callback rate?',
      medical: 'Which treatments have the highest patient satisfaction scores?',
      valet: 'Which EV charging stations have the longest queue times?',
      logistics: 'Which routes have the highest fuel cost variance?',
      realty: 'Which property types have the fastest time to close?',
      banking: 'Which product categories have the highest default probability?',
      grocery: 'Which product categories have the highest margin?',
      general: 'Which products or services generate the highest margin?',
    },
  },
  execute: {
    label: 'Execute',
    icon: '⚡',
    purpose: 'Run a query or trigger a workflow',
    examples: {
      restaurant: 'What is our food cost percentage by menu category this month?',
      lawn: 'What is our revenue per crew member per day?',
      hvac: 'What is our first-time fix rate by technician?',
      insurance: 'What is our claims-to-premium ratio by policy type?',
      plumbing: 'What is our average time-to-dispatch by job priority?',
      medical: 'What is our average revenue per patient visit by insurance type?',
      valet: 'What is our average vehicle dwell time by day of week?',
      logistics: 'What is our cost per delivery by service type?',
      realty: 'What is our average list-to-sale price ratio by neighborhood?',
      banking: 'What is our current loan-to-deposit ratio?',
      grocery: "What are today's total sales across both stores?",
      general: 'What is our month-over-month revenue growth rate?',
    },
  },
  monitor: {
    label: 'Monitor Outcome',
    icon: '📊',
    purpose: 'Track results after an action is taken',
    examples: {
      restaurant: 'How does our average check size compare across lunch vs dinner?',
      lawn: 'How does weather impact our weekly job completion rate?',
      hvac: 'How does seasonal demand affect our parts inventory turnover?',
      insurance: 'How does our loss ratio trend quarter over quarter?',
      plumbing: 'How does franchise vs retail subscriber revenue trend monthly?',
      medical: 'How does treatment room utilization vary by time of day?',
      valet: 'How does mall event traffic impact valet demand?',
      logistics: 'How does driver experience correlate with delivery speed?',
      realty: 'How does staging affect days on market?',
      banking: 'What is our net interest margin trend over the last 6 months?',
      grocery: 'Compare retail vs dine-in vs takeaway revenue this month',
      general: 'How does our staffing level correlate with revenue per day?',
    },
  },
  decision: {
    label: 'Decision To be Made',
    icon: '🤔',
    purpose: 'Requires human review and a decision before proceeding',
    examples: {
      restaurant: 'Which promotions drove the highest incremental revenue?',
      lawn: 'Which customers are at risk of churning based on service frequency decline?',
      hvac: 'How many emergency calls convert to maintenance contracts?',
      insurance: 'How many policies are within 60 days of renewal?',
      plumbing: 'Which areas have unmet demand based on declined jobs?',
      medical: 'How many patients are overdue for follow-up appointments?',
      valet: 'How many repeat valet customers do we have this month?',
      logistics: 'Which contractor agencies provide the best on-time performance?',
      realty: 'What is our lease renewal rate by property type?',
      banking: 'What is our Tier 1 capital ratio vs regulatory minimum?',
      grocery: 'Which perishable products have the highest waste rate?',
      general: 'What are the leading indicators of customer churn in our data?',
    },
  },
};

/**
 * SelfLearningBadge — Shows source badge on query results resolved by self-learning.
 * Badge text: "Self-Learning SQL Intelligence: [SQL statement]"
 */
const SelfLearningBadge = ({ source, sql }) => {
  if (!source || !source.startsWith('self_learned_')) return null;
  const levelMap = {
    self_learned_L1: { label: '🔄 Self-Learned L1', bg: '#fff3e0', border: '#ffcc80', color: '#E65100' },
    self_learned_L2: { label: '✅ Validated L2', bg: '#e8f5e9', border: '#a5d6a7', color: '#1B5E20' },
    self_learned_L3: { label: '🔒 Permanent L3', bg: '#e3f2fd', border: '#90caf9', color: '#0D47A1' },
  };
  const cfg = levelMap[source] || levelMap.self_learned_L1;
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '4px 10px', background: cfg.bg, border: `1px solid ${cfg.border}`, borderRadius: '8px', fontSize: '11px', color: cfg.color, fontWeight: 600, marginTop: '4px' }}>
      <span>{cfg.label}</span>
      {sql && <span style={{ fontFamily: 'monospace', fontSize: '10px', fontWeight: 400, maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>Self-Learning SQL Intelligence: {sql.slice(0, 80)}{sql.length > 80 ? '…' : ''}</span>}
    </div>
  );
};

/**
 * SelfLearningPatterns — Manages self-learning patterns section within CustomIntelligencePanel.
 */
const SelfLearningPatterns = ({ clientId, onRunQuestion, chipStyle, dd, rowStyle, iconBtn }) => {
  const [showSL, setShowSL] = React.useState(false);
  const [patterns, setPatterns] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [editingSLId, setEditingSLId] = React.useState(null);
  const [editSLSql, setEditSLSql] = React.useState('');

  const fetchPatterns = React.useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    try {
      const resp = await fetch(`/v1/clients/${clientId}/self-learning/patterns`);
      const data = await resp.json();
      if (data.status === 'ok') setPatterns(data.patterns || []);
    } catch (err) {
      console.error('Failed to fetch self-learning patterns:', err);
    }
    setLoading(false);
  }, [clientId]);

  const handleApprove = async (sig) => {
    try {
      await fetch(`/v1/clients/${clientId}/self-learning/approve/${encodeURIComponent(sig)}`, { method: 'POST' });
      fetchPatterns();
    } catch (err) { console.error('Approve failed:', err); }
  };

  const handleRemove = async (id) => {
    try {
      await fetch(`/v1/clients/${clientId}/self-learning/patterns/${encodeURIComponent(id)}`, { method: 'DELETE' });
      fetchPatterns();
    } catch (err) { console.error('Remove failed:', err); }
  };

  const handleEditSql = async (qid) => {
    try {
      await fetch(`/v1/clients/${clientId}/self-learning/patterns/${encodeURIComponent(qid)}/sql`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sql: editSLSql }),
      });
      setEditingSLId(null);
      setEditSLSql('');
      fetchPatterns();
    } catch (err) { console.error('Edit SQL failed:', err); }
  };

  const handlePin = async (questionId, sql, atomicTopic) => {
    try {
      await fetch(`/v1/clients/${clientId}/self-learning/pin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ question_id: questionId, sql, atomic_topic: atomicTopic || '' }),
      });
      fetchPatterns();
    } catch (err) { console.error('Pin failed:', err); }
  };

  const levelBadge = (level) => {
    if (level === 1) return <span style={{ fontSize: '10px', padding: '1px 6px', background: '#fff3e0', border: '1px solid #ffcc80', borderRadius: '8px', color: '#E65100', fontWeight: 600 }}>🔄 L1</span>;
    if (level === 2) return <span style={{ fontSize: '10px', padding: '1px 6px', background: '#e8f5e9', border: '1px solid #a5d6a7', borderRadius: '8px', color: '#1B5E20', fontWeight: 600 }}>✅ L2</span>;
    return <span style={{ fontSize: '10px', padding: '1px 6px', background: '#e3f2fd', border: '1px solid #90caf9', borderRadius: '8px', color: '#0D47A1', fontWeight: 600 }}>🔒 L3</span>;
  };

  const l1 = patterns.filter(p => p.level === 1);
  const l2 = patterns.filter(p => p.level === 2);
  const l3 = patterns.filter(p => p.level === 3);

  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <button onClick={() => { setShowSL(p => !p); if (!showSL) fetchPatterns(); }}
        style={chipStyle('#fce4ec', '#f48fb1', '#880E4F')}>
        🧠 Self-Learning ▾
      </button>
      {showSL && (
        <div style={dd}>
          <div style={{ padding: '10px 14px', borderBottom: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontWeight: 700, fontSize: '13px' }}>🧠 Self-Learning Patterns</span>
            <button onClick={fetchPatterns} style={{ background: 'none', border: '1px solid #ddd', borderRadius: '6px', cursor: 'pointer', padding: '3px 8px', fontSize: '12px' }}>🔄</button>
          </div>
          {loading ? (
            <div style={{ padding: '16px', textAlign: 'center', color: '#888', fontSize: '12px' }}>Loading...</div>
          ) : patterns.length === 0 ? (
            <div style={{ padding: '16px', textAlign: 'center', color: '#999', fontSize: '12px' }}>No self-learning patterns yet. Patterns are auto-captured when the LLM retry loop corrects SQL.</div>
          ) : (
            <>
              {/* L3 Permanent */}
              {l3.length > 0 && (
                <div>
                  <div style={{ padding: '4px 14px', fontSize: '10px', fontWeight: 700, color: '#0D47A1', background: '#e3f2fd', textTransform: 'uppercase' }}>🔒 Level 3 — Permanent ({l3.length})</div>
                  {l3.map(p => (
                    <div key={p.question_id || p.signature} style={rowStyle}>
                      {editingSLId === (p.question_id || p.signature) ? (
                        <div style={{ flex: 1, padding: '4px 0' }}>
                          <textarea value={editSLSql} onChange={e => setEditSLSql(e.target.value)} rows={3}
                            style={{ width: '100%', padding: '4px 8px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '10px', fontFamily: 'monospace', boxSizing: 'border-box' }} />
                          <button onClick={() => handleEditSql(p.question_id)} style={{ ...iconBtn, color: '#4CAF50', fontSize: '11px', fontWeight: 700 }}>✓ Save</button>
                          <button onClick={() => setEditingSLId(null)} style={{ ...iconBtn, color: '#999', fontSize: '11px', marginLeft: '6px' }}>Cancel</button>
                        </div>
                      ) : (
                        <>
                          {levelBadge(3)}
                          <button onClick={() => onRunQuestion(p.query)} style={{ flex: 1, background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', fontSize: '11px', color: '#333', padding: '2px 4px' }}>
                            <div style={{ fontWeight: 500 }}>{(p.query || '').slice(0, 70)}</div>
                            <div style={{ fontSize: '9px', color: '#1565C0', fontFamily: 'monospace' }}>{(p.sql || '').slice(0, 60)}...</div>
                          </button>
                          <span style={{ fontSize: '9px', color: '#aaa' }}>{p.success_count}×</span>
                          <button onClick={() => { setEditingSLId(p.question_id || p.signature); setEditSLSql(p.sql || ''); }} title="Edit SQL" style={iconBtn}>✏️</button>
                          <button onClick={() => handleRemove(p.question_id || p.signature)} title="Remove" style={{ ...iconBtn, color: '#ccc' }}>🗑</button>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {/* L2 Validated */}
              {l2.length > 0 && (
                <div>
                  <div style={{ padding: '4px 14px', fontSize: '10px', fontWeight: 700, color: '#1B5E20', background: '#e8f5e9', textTransform: 'uppercase' }}>✅ Level 2 — Validated ({l2.length})</div>
                  {l2.map(p => (
                    <div key={p.signature} style={rowStyle}>
                      {levelBadge(2)}
                      <button onClick={() => onRunQuestion(p.query)} style={{ flex: 1, background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', fontSize: '11px', color: '#333', padding: '2px 4px' }}>
                        <div style={{ fontWeight: 500 }}>{(p.query || '').slice(0, 70)}</div>
                        <div style={{ fontSize: '9px', color: '#1565C0', fontFamily: 'monospace' }}>{(p.sql || '').slice(0, 60)}...</div>
                      </button>
                      <span style={{ fontSize: '9px', color: '#aaa' }}>{p.success_count}×</span>
                      <button onClick={() => handleApprove(p.signature)} title="Approve → L3" style={{ ...iconBtn, color: '#4CAF50' }}>✅</button>
                      <button onClick={() => handleRemove(p.signature)} title="Remove" style={{ ...iconBtn, color: '#ccc' }}>🗑</button>
                    </div>
                  ))}
                </div>
              )}
              {/* L1 Candidate */}
              {l1.length > 0 && (
                <div>
                  <div style={{ padding: '4px 14px', fontSize: '10px', fontWeight: 700, color: '#E65100', background: '#fff3e0', textTransform: 'uppercase' }}>🔄 Level 1 — Candidate ({l1.length})</div>
                  {l1.map(p => (
                    <div key={p.signature} style={rowStyle}>
                      {levelBadge(1)}
                      <button onClick={() => onRunQuestion(p.query)} style={{ flex: 1, background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', fontSize: '11px', color: '#333', padding: '2px 4px' }}>
                        <div style={{ fontWeight: 500 }}>{(p.query || '').slice(0, 70)}</div>
                        <div style={{ fontSize: '9px', color: '#1565C0', fontFamily: 'monospace' }}>{(p.sql || '').slice(0, 60)}...</div>
                      </button>
                      <span style={{ fontSize: '9px', color: '#aaa' }}>{p.success_count}×</span>
                      <button onClick={() => handleRemove(p.signature)} title="Remove" style={{ ...iconBtn, color: '#ccc' }}>🗑</button>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * SelfLearningUseCases — Self-Intelligence Report panel.
 * Displays top business use cases with Step 1/2/3 diagnosis,
 * self-learning patterns, SQL intelligence history,
 * and provides download buttons for PDF/Excel export of full history.
 */
const SelfLearningUseCases = ({ clientId, chipStyle, dd, onRunQuestion, rowStyle, iconBtn }) => {
  const [showUC, setShowUC] = React.useState(false);
  const [useCases, setUseCases] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [fromDate, setFromDate] = React.useState('');
  const [toDate, setToDate] = React.useState('');
  const [exporting, setExporting] = React.useState(null);
  // Self-Learning Patterns state (merged from SelfLearningPatterns)
  const [patterns, setPatterns] = React.useState([]);
  const [patternsLoading, setPatternsLoading] = React.useState(false);
  const [activeSection, setActiveSection] = React.useState('patterns'); // 'patterns' | 'usecases' | 'export'

  const fetchUseCases = React.useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    try {
      const resp = await fetch(`/v1/clients/${clientId}/self-learning/use-cases?limit=2`);
      const data = await resp.json();
      if (data.status === 'ok') setUseCases(data.use_cases || []);
    } catch (err) {
      console.error('Failed to fetch use cases:', err);
    }
    setLoading(false);
  }, [clientId]);

  const fetchPatterns = React.useCallback(async () => {
    if (!clientId) return;
    setPatternsLoading(true);
    try {
      const resp = await fetch(`/v1/clients/${clientId}/self-learning/patterns`);
      const data = await resp.json();
      if (data.status === 'ok') setPatterns(data.patterns || []);
    } catch (err) {
      console.error('Failed to fetch self-learning patterns:', err);
    }
    setPatternsLoading(false);
  }, [clientId]);

  const handleExport = async (format) => {
    setExporting(format);
    try {
      const params = new URLSearchParams();
      if (fromDate) params.set('from_date', fromDate);
      if (toDate) params.set('to_date', toDate);
      const resp = await fetch(`/v1/clients/${clientId}/self-learning/export/${format}?${params}`);
      if (resp.ok) {
        const blob = await resp.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `self-learning-${clientId}-${fromDate || 'all'}-to-${toDate || 'now'}.${format === 'excel' ? 'xlsx' : 'pdf'}`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } else {
        console.error('Export failed:', resp.status);
      }
    } catch (err) {
      console.error('Export error:', err);
    }
    setExporting(null);
  };

  const stepIcon = (step) => {
    if (step === 1) return '📋';
    if (step === 2) return '⚠️';
    return '✅';
  };

  const stepColor = (step) => {
    if (step === 1) return { bg: '#E3F2FD', border: '#90CAF9', text: '#0D47A1' };
    if (step === 2) return { bg: '#FFF3E0', border: '#FFCC80', text: '#E65100' };
    return { bg: '#E8F5E9', border: '#A5D6A7', text: '#1B5E20' };
  };

  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <button onClick={() => { setShowUC(p => !p); if (!showUC) { fetchPatterns(); fetchUseCases(); } }}
        style={chipStyle('#E8EAF6', '#9FA8DA', '#283593')}>
        📊 Self-Learning Report ▾
      </button>
      {showUC && (
        <div style={{ ...dd, width: '480px' }}>
          {/* Header */}
          <div style={{ padding: '10px 14px', borderBottom: '1px solid #e0e0e0', background: '#FAFAFA' }}>
            <div style={{ fontWeight: 700, fontSize: '13px', color: '#283593' }}>📊 Self-Learning Report</div>
            <div style={{ fontSize: '10px', color: '#888', marginTop: '2px' }}>Self-learning patterns, business use cases with diagnosis steps, and SQL intelligence history</div>
          </div>

          {/* Section Tabs */}
          <div style={{ display: 'flex', borderBottom: '1px solid #e0e0e0' }}>
            {[
              { key: 'patterns', label: '🧠 Patterns', count: patterns.length },
              { key: 'usecases', label: '📊 Use Cases', count: useCases.length },
              { key: 'export', label: '📥 Export' },
            ].map(tab => (
              <button key={tab.key} onClick={() => setActiveSection(tab.key)}
                style={{ flex: 1, padding: '8px 4px', border: 'none', borderBottom: activeSection === tab.key ? '2px solid #283593' : '2px solid transparent', background: activeSection === tab.key ? '#E8EAF6' : 'transparent', cursor: 'pointer', fontSize: '11px', fontWeight: activeSection === tab.key ? 700 : 400, color: activeSection === tab.key ? '#283593' : '#666' }}>
                {tab.label}{tab.count !== undefined ? ` (${tab.count})` : ''}
              </button>
            ))}
          </div>

          {/* Patterns Section */}
          {activeSection === 'patterns' && (
            <div style={{ maxHeight: '320px', overflowY: 'auto' }}>
              {patternsLoading ? (
                <div style={{ padding: '16px', textAlign: 'center', color: '#888', fontSize: '12px' }}>Loading...</div>
              ) : patterns.length === 0 ? (
                <div style={{ padding: '16px', textAlign: 'center', color: '#999', fontSize: '12px' }}>No self-learning patterns yet. Patterns are auto-captured when the LLM retry loop corrects SQL.</div>
              ) : (
                patterns.map((p, idx) => (
                  <div key={p.question_id || idx} style={{ padding: '8px 14px', borderBottom: '1px solid #f0f0f0', fontSize: '12px' }}>
                    <div style={{ fontWeight: 600, color: '#333', marginBottom: '4px' }}>{(p.question || '').slice(0, 100)}</div>
                    <div style={{ fontFamily: 'monospace', fontSize: '10px', color: '#1565C0', background: '#F5F5F5', padding: '4px 6px', borderRadius: '4px', marginBottom: '4px', wordBreak: 'break-all' }}>{(p.sql || '').slice(0, 150)}{(p.sql || '').length > 150 ? '…' : ''}</div>
                    <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                      <span style={{ fontSize: '9px', color: p.approved ? '#2E7D32' : '#F57F17', fontWeight: 600 }}>{p.approved ? '✅ Approved' : '⏳ Pending'}</span>
                      {onRunQuestion && <button onClick={() => onRunQuestion(p.question)} style={{ fontSize: '10px', padding: '2px 8px', border: '1px solid #ddd', borderRadius: '4px', background: '#fff', cursor: 'pointer' }}>▶ Run</button>}
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {/* Use Cases Section */}
          {activeSection === 'usecases' && (
            <>
          {loading ? (
            <div style={{ padding: '16px', textAlign: 'center', color: '#888', fontSize: '12px' }}>Loading...</div>
          ) : useCases.length === 0 ? (
            <div style={{ padding: '16px', textAlign: 'center', color: '#999', fontSize: '12px' }}>
              No self-learning use cases yet. Use cases are recorded when SQL queries go through the diagnosis cycle.
            </div>
          ) : (
            <div style={{ maxHeight: '320px', overflowY: 'auto' }}>
              {useCases.map((uc, idx) => (
                <div key={idx} style={{ padding: '10px 14px', borderBottom: '1px solid #f0f0f0' }}>
                  <div style={{ fontSize: '11px', fontWeight: 600, color: '#333', marginBottom: '6px' }}>
                    {(uc.query || '').replace(/\[.*?\]/g, '').trim().slice(0, 100)}
                  </div>
                  <div style={{ fontSize: '9px', color: '#1565C0', fontFamily: 'monospace', marginBottom: '8px', background: '#F5F5F5', padding: '4px 6px', borderRadius: '4px', wordBreak: 'break-all' }}>
                    {(uc.sql || '').slice(0, 150)}{(uc.sql || '').length > 150 ? '…' : ''}
                  </div>
                  {/* 3 Steps */}
                  {[1, 2, 3].map(stepNum => {
                    const step = (uc.steps || [])[stepNum - 1] || {};
                    const sc = stepColor(stepNum);
                    const detail = step.detail || (stepNum === 1 ? 'Tables determined' : stepNum === 2 ? 'No error on this run' : 'SQL executed successfully');
                    return (
                      <div key={stepNum} style={{ display: 'flex', alignItems: 'flex-start', gap: '6px', marginBottom: '4px', padding: '4px 8px', background: sc.bg, border: `1px solid ${sc.border}`, borderRadius: '6px' }}>
                        <span style={{ fontSize: '12px', flexShrink: 0 }}>{stepIcon(stepNum)}</span>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: '10px', fontWeight: 600, color: sc.text }}>Step {stepNum}</div>
                          <div style={{ fontSize: '10px', color: '#555' }}>{detail.slice(0, 200)}</div>
                        </div>
                        {step.timestamp && (
                          <span style={{ fontSize: '8px', color: '#aaa', flexShrink: 0 }}>{step.timestamp.slice(0, 16)}</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          )}
          </>
          )}

          {/* Export Section */}
          {activeSection === 'export' && (
          <div style={{ padding: '10px 14px', borderTop: '1px solid #e0e0e0', background: '#FAFAFA' }}>
            <div style={{ fontSize: '10px', fontWeight: 700, color: '#555', marginBottom: '6px' }}>📥 Download Full History</div>
            <div style={{ display: 'flex', gap: '6px', alignItems: 'center', marginBottom: '6px' }}>
              <label style={{ fontSize: '10px', color: '#666' }}>From:</label>
              <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)}
                style={{ fontSize: '10px', padding: '2px 4px', border: '1px solid #ddd', borderRadius: '4px' }} />
              <label style={{ fontSize: '10px', color: '#666' }}>To:</label>
              <input type="date" value={toDate} onChange={e => setToDate(e.target.value)}
                style={{ fontSize: '10px', padding: '2px 4px', border: '1px solid #ddd', borderRadius: '4px' }} />
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button onClick={() => handleExport('excel')} disabled={exporting === 'excel'}
                style={{ flex: 1, padding: '6px 10px', background: '#1B5E20', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '11px', fontWeight: 600, cursor: 'pointer', opacity: exporting === 'excel' ? 0.6 : 1 }}>
                {exporting === 'excel' ? '⏳ Exporting...' : '📗 Download Excel'}
              </button>
              <button onClick={() => handleExport('pdf')} disabled={exporting === 'pdf'}
                style={{ flex: 1, padding: '6px 10px', background: '#B71C1C', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '11px', fontWeight: 600, cursor: 'pointer', opacity: exporting === 'pdf' ? 0.6 : 1 }}>
                {exporting === 'pdf' ? '⏳ Exporting...' : '📕 Download PDF'}
              </button>
            </div>
            {!fromDate && !toDate && (
              <div style={{ fontSize: '9px', color: '#999', marginTop: '4px', textAlign: 'center' }}>
                Leave dates empty to download all history
              </div>
            )}
          </div>
          )}
        </div>
      )}
    </div>
  );
};

const CustomIntelligencePanel = ({
  customQuestions, setCustomQuestions, sharedQuestions,
  clientId, companyName, product, onRunQuestion, onGenaiQuery,
  operationsMenu, getDemoResponse,
  llmProviderId, onProviderChange, llmProviders,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [newQuestion, setNewQuestion] = useState('');
  const [newLabel, setNewLabel] = useState('');
  const [newInstruction, setNewInstruction] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editQuestion, setEditQuestion] = useState('');
  const [editLabel, setEditLabel] = useState('');
  const [editInstruction, setEditInstruction] = useState('');
  const [showLLM, setShowLLM] = useState(false);
  const llmProvider = llmProviderId || 'gemini';
  const activeLlmProviders = llmProviders || LLM_PROVIDERS;
  const [llmQuestions, setLlmQuestions] = useState(null);
  const [llmRefreshing, setLlmRefreshing] = useState(false);
  const [showSummary, setShowSummary] = useState(false);
  const [summaryResult, setSummaryResult] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [showCustomOpen, setShowCustomOpen] = useState(false);
  // Share consent state — requires explicit approval per question
  const [shareConsentId, setShareConsentId] = useState(null);
  // Shared BI dropdown state
  const [showSharedBI, setShowSharedBI] = useState(false);
  const [expandedSharedCompany, setExpandedSharedCompany] = useState(null);
  // Atomic Intelligence state
  const [atomicTooltip, setAtomicTooltip] = useState(null);

  // CID Tooltip state for AI Top-10 chip
  const [cidTooltipVisible, setCidTooltipVisible] = useState(false);
  const aiTop10ChipRef = useRef(null);

  // Deep-dive LLM session state (for AI Top-10 menu questions)
  const [deepDiveQuestion, setDeepDiveQuestion] = useState(null);

  // DDLS consent modal state
  const [ddlsConsentPending, setDdlsConsentPending] = useState(null); // { question, provider } or null

  // Derive product_type for deep-dive from the product prop
  const productType = product || '';

  // Derive product display name for AI Top-10 title
  const productDisplayName = (() => {
    if (!product) return 'BI Questions';
    const entry = PRODUCT_DB_MAP[product];
    if (entry && entry.displayName) return entry.displayName;
    if (entry && entry.description) return entry.description.split('—')[0].trim();
    // Fallback: use the product slug as-is
    return product;
  })();

  // Group shared questions by company name for the Shared BI dropdown
  const sharedBIGrouped = React.useMemo(() => {
    if (!sharedQuestions || sharedQuestions.length === 0) return [];
    const groups = {};
    for (const q of sharedQuestions) {
      const company = q.shared_by_name || 'Unknown';
      if (!groups[company]) groups[company] = [];
      groups[company].push(q);
    }
    return Object.entries(groups).map(([company, questions]) => ({
      company,
      companyShort: company.length > 20 ? company.slice(0, 18) + '…' : company,
      questions,
    }));
  }, [sharedQuestions]);

  const bkey = ['manufacturing', 'predictivemaintenance', 'restaurant', 'lawn', 'hvac', 'insurance', 'plumbing', 'medical', 'wellness', 'valet', 'auto', 'logistics', 'freight', 'realty', 'real estate', 'bank', 'financial', 'grocery', 'supermarket', 'china-us', 'strategic-intel', 'geopulse', 'geopolitical', 'procurement'].find(k => product.includes(k)) || 'general';

  // Load/reload LLM questions (called on open, provider change, or manual refresh)
  // Calls the same backend API as the Query button blotter (LLMTop10) to get
  // product-type-context-aware BUCI questions instead of static hardcoded ones.
  const loadLlmQuestions = React.useCallback(() => {
    setLlmRefreshing(true);
    setLlmQuestions(null);

    // Call backend API for product-type-aware questions
    const ciQuestions = new Set(customQuestions.map(q => q.question.toLowerCase().trim()));
    fetch(`${API_BASE}/clients/${clientId}/top10-questions`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        product_type: product || '',
        exclude_questions: customQuestions.map(q => q.question),
        llm_provider: llmProvider,
      }),
    })
      .then(res => res.ok ? res.json() : null)
      .then(data => {
        if (data && data.questions && data.questions.length > 0) {
          // Backend returns {questions: [{q: "...", ...}, ...]}
          const questions = data.questions.map(item => typeof item === 'string' ? item : item.q || item.question || '');
          const available = questions.filter(q => q && !ciQuestions.has(q.toLowerCase().trim()));
          setLlmQuestions(available.slice(0, 10));
        } else {
          // Fallback to static questions if backend unavailable
          const keyMap = { wellness: 'medical', auto: 'valet', freight: 'logistics', 'real estate': 'realty', bank: 'banking', financial: 'banking', supermarket: 'grocery', 'china-us': 'geopolitical', 'strategic-intel': 'geopolitical', 'geopulse': 'geopolitical' };
          const resolvedKey = keyMap[bkey] || bkey;
          const allQuestions = getQuestionsForProvider(llmProvider, resolvedKey);
          const available = allQuestions.filter(q => !ciQuestions.has(q.toLowerCase().trim()));
          setLlmQuestions(available.slice(0, 10));
        }
        setLlmRefreshing(false);
      })
      .catch(() => {
        // Network error — fall back to static questions
        const keyMap = { wellness: 'medical', auto: 'valet', freight: 'logistics', 'real estate': 'realty', bank: 'banking', financial: 'banking', supermarket: 'grocery', 'china-us': 'geopolitical', 'strategic-intel': 'geopolitical', 'geopulse': 'geopolitical' };
        const resolvedKey = keyMap[bkey] || bkey;
        const allQuestions = getQuestionsForProvider(llmProvider, resolvedKey);
        const available = allQuestions.filter(q => !ciQuestions.has(q.toLowerCase().trim()));
        setLlmQuestions(available.slice(0, 10));
        setLlmRefreshing(false);
      });
  }, [clientId, product, bkey, llmProvider, customQuestions]);

  // Auto-refresh when LLM provider changes
  const prevProvider = React.useRef(llmProvider);
  React.useEffect(() => {
    if (prevProvider.current !== llmProvider) {
      prevProvider.current = llmProvider;
      loadLlmQuestions();
    }
  }, [llmProvider, loadLlmQuestions]);

  const filteredCustom = customQuestions.filter(q =>
    q.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (q.label || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredShared = sharedQuestions.filter(q =>
    q.question.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const saveToLocal = (updated) => {
    setCustomQuestions(updated);
    localStorage.setItem(`ci_${clientId}`, JSON.stringify(updated));
  };

  const handleAdd = async () => {
    if (!newQuestion.trim()) return;
    const entry = {
      question_id: `cq-${Date.now()}`, question: newQuestion.trim(),
      label: newLabel.trim() || newQuestion.trim().slice(0, 50),
      instruction: newInstruction.trim(), category: 'custom',
      is_favorite: false, use_count: 0, is_shared: false,
      share_approved: false, shared_by_name: companyName,
      created_at: new Date().toISOString(),
    };
    saveToLocal([entry, ...customQuestions]);
    setNewQuestion(''); setNewLabel(''); setNewInstruction(''); setShowAddForm(false);

    // Persist to backend
    try {
      const resp = await fetch(`/v1/clients/${clientId}/custom-intelligence`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          question: entry.question,
          label: entry.label,
          instruction: entry.instruction,
          category: entry.category,
        }),
      });
      if (resp.ok) {
        const result = await resp.json();
        // Update local entry with server-assigned question_id if returned
        if (result.question_id) {
          const updated = customQuestions.map(q =>
            q.question_id === entry.question_id ? { ...q, question_id: result.question_id } : q
          );
          saveToLocal([{ ...entry, question_id: result.question_id }, ...updated.filter(q => q.question_id !== entry.question_id)]);
        }
      }
    } catch (err) {
      console.warn('Backend save failed, question saved locally:', err);
    }
  };

  const handleUpdate = (qid) => {
    const updated = customQuestions.map(q => q.question_id === qid
      ? { ...q, question: editQuestion, label: editLabel, instruction: editInstruction, updated_at: new Date().toISOString() }
      : q);
    saveToLocal(updated);
    setEditingId(null);
  };

  const handleDelete = (qid) => {
    saveToLocal(customQuestions.filter(q => q.question_id !== qid));
  };

  const handleToggleFav = (qid) => {
    const updated = customQuestions.map(q => q.question_id === qid ? { ...q, is_favorite: !q.is_favorite } : q)
      .sort((a, b) => (b.is_favorite ? 1 : 0) - (a.is_favorite ? 1 : 0) || b.use_count - a.use_count);
    saveToLocal(updated);
  };

  const handleShare = (qid) => {
    // Only share the question text — NEVER share instructions (SQL/NLP)
    const updated = customQuestions.map(q => q.question_id === qid
      ? { ...q, is_shared: true, share_approved: true, shared_instruction: '' }
      : q);
    saveToLocal(updated);
    setShareConsentId(null); // close consent prompt
  };

  const handleUnshare = (qid) => {
    const updated = customQuestions.map(q => q.question_id === qid
      ? { ...q, is_shared: false, share_approved: false }
      : q);
    saveToLocal(updated);
  };

  const handleRun = (q) => {
    const updated = customQuestions.map(cq => cq.question_id === q.question_id ? { ...cq, use_count: cq.use_count + 1 } : cq);
    saveToLocal(updated);
    const queryWithInstruction = q.instruction ? `${q.question}\n\n[Instruction: ${q.instruction}]` : q.question;
    onRunQuestion(queryWithInstruction);
  };

  const handleAdoptLlm = (question) => {
    const entry = {
      question_id: `cq-${Date.now()}`, question, label: question.slice(0, 60),
      instruction: '', category: 'llm_suggested', is_favorite: false,
      use_count: 0, is_shared: false, share_approved: false,
      shared_by_name: companyName, created_at: new Date().toISOString(),
    };
    saveToLocal([entry, ...customQuestions]);
    // Remove adopted question from LLM list and backfill
    setLlmQuestions(prev => {
      if (!prev) return prev;
      const remaining = prev.filter(q => q !== question);
      // Backfill: get the full pool, exclude all CI questions + current LLM list
      const keyMap = { wellness: 'medical', auto: 'valet', freight: 'logistics', 'real estate': 'realty', bank: 'banking', financial: 'banking', supermarket: 'grocery', 'china-us': 'geopolitical', 'strategic-intel': 'geopolitical', 'geopulse': 'geopolitical' };
      const resolvedKey = keyMap[bkey] || bkey;
      const allPool = getQuestionsForProvider(llmProvider, resolvedKey);
      const ciSet = new Set([...customQuestions.map(q => q.question.toLowerCase().trim()), question.toLowerCase().trim()]);
      const remainingSet = new Set(remaining.map(q => q.toLowerCase().trim()));
      const backfill = allPool.filter(q => !ciSet.has(q.toLowerCase().trim()) && !remainingSet.has(q.toLowerCase().trim()));
      // Add one backfill question if available
      if (backfill.length > 0 && remaining.length < 10) {
        remaining.push(backfill[0]);
      }
      return remaining.slice(0, 10);
    });
  };

  // --- Atomic Intelligence handlers ---
  const atomicIndustryKeyMap = { wellness: 'medical', auto: 'valet', freight: 'logistics', 'real estate': 'realty', bank: 'banking', financial: 'banking', supermarket: 'grocery' };
  const atomicIndustryKey = atomicIndustryKeyMap[bkey] || bkey;

  // Partition customQuestions by atomic_topic for the Atomic Intelligence section
  const atomicGroups = React.useMemo(() => {
    const groups = { unassigned: [], recommend: [], execute: [], monitor: [], decision: [] };
    for (const q of customQuestions) {
      const topic = q.atomic_topic;
      if (topic && groups[topic]) {
        groups[topic].push(q);
      }
    }
    return groups;
  }, [customQuestions]);

  const handleStageForAtomic = (question) => {
    // Adopt an LLM question into custom_intelligence with atomic_topic = null (unassigned staging)
    const entry = {
      question_id: `cq-${Date.now()}`, question, label: question.slice(0, 60),
      instruction: '', category: 'llm_suggested', is_favorite: false,
      use_count: 0, is_shared: false, share_approved: false,
      shared_by_name: companyName, atomic_topic: null,
      created_at: new Date().toISOString(),
    };
    saveToLocal([entry, ...customQuestions]);
    // Remove from LLM pool with backfill (same pattern as handleAdoptLlm)
    setLlmQuestions(prev => {
      if (!prev) return prev;
      const remaining = prev.filter(q => q !== question);
      const keyMap = { wellness: 'medical', auto: 'valet', freight: 'logistics', 'real estate': 'realty', bank: 'banking', financial: 'banking', supermarket: 'grocery', 'china-us': 'geopolitical', 'strategic-intel': 'geopolitical', 'geopulse': 'geopolitical' };
      const resolvedKey = keyMap[bkey] || bkey;
      const allPool = getQuestionsForProvider(llmProvider, resolvedKey);
      const ciSet = new Set([...customQuestions.map(q => q.question.toLowerCase().trim()), question.toLowerCase().trim()]);
      const remainingSet = new Set(remaining.map(q => q.toLowerCase().trim()));
      const backfill = allPool.filter(q => !ciSet.has(q.toLowerCase().trim()) && !remainingSet.has(q.toLowerCase().trim()));
      if (backfill.length > 0 && remaining.length < 10) {
        remaining.push(backfill[0]);
      }
      return remaining.slice(0, 10);
    });
  };

  const handleAssignTopic = async (questionId, newTopic) => {
    // Optimistic UI: update local state immediately
    const previousQuestions = [...customQuestions];
    const updated = customQuestions.map(q =>
      q.question_id === questionId ? { ...q, atomic_topic: newTopic } : q
    );
    saveToLocal(updated);

    // Call backend API
    try {
      const resp = await fetch(
        `/v1/clients/${clientId}/custom-intelligence/${questionId}/atomic-topic`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ atomic_topic: newTopic }),
        }
      );
      if (!resp.ok) {
        throw new Error('API failed');
      }
    } catch (err) {
      // Rollback on failure
      saveToLocal(previousQuestions);
      console.error('Failed to assign atomic topic:', err);
    }
  };

  const handleSummary = async () => {
    setSummaryLoading(true); setSummaryResult(null); setShowSummary(true);
    // Run all operations + custom questions and compile summary
    const allQueries = [
      ...operationsMenu.map(op => op.query),
      ...customQuestions.map(q => q.instruction ? `${q.question} [Instruction: ${q.instruction}]` : q.question),
    ];
    const results = [];
    for (const q of allQueries) {
      const demo = getDemoResponse(q);
      results.push({ query: q.slice(0, 80), answer: demo.answer.slice(0, 200) });
    }
    setSummaryResult({
      generated_at: new Date().toISOString(),
      total_queries: allQueries.length,
      operations_count: operationsMenu.length,
      custom_count: customQuestions.length,
      results,
    });
    setSummaryLoading(false);
  };

  const chipStyle = (bg, border, color) => ({
    padding: '4px 8px', background: bg, border: `1px solid ${border}`, borderRadius: '14px',
    cursor: 'pointer', fontSize: '11px', fontWeight: 600, color, display: 'inline-flex', alignItems: 'center', gap: '3px',
  });
  const dd = { position: 'absolute', top: '34px', left: 0, zIndex: 100, width: '460px', background: '#fff', border: '1px solid #e0e0e0', borderRadius: '10px', boxShadow: '0 4px 20px rgba(0,0,0,0.15)', maxHeight: '500px', overflowY: 'auto' };
  const rowStyle = { padding: '6px 14px', display: 'flex', alignItems: 'center', gap: '6px', borderBottom: '1px solid #fafafa' };
  const iconBtn = { background: 'none', border: 'none', cursor: 'pointer', fontSize: '13px', padding: '2px' };

  return (
    <>
      {/* Custom Intelligence Dropdown */}
      <div style={{ position: 'relative', display: 'inline-block' }}>
        <button onClick={() => { setShowLLM(false); setShowSummary(false); setShowSharedBI(false); setShowCustomOpen(p => !p); setSearchTerm(''); }}
          style={chipStyle('#fff3e0', '#ffcc80', '#E65100')}>
          💡 Custom Intelligence ▾
        </button>
        {showCustomOpen && (
          <div style={dd}>
            <div style={{ padding: '10px 14px', borderBottom: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontWeight: 700, fontSize: '13px' }}>💡 Custom Intelligence</span>
              <button onClick={() => setShowAddForm(!showAddForm)} style={{
                padding: '4px 10px', background: '#E65100', color: '#fff', border: 'none',
                borderRadius: '6px', fontSize: '11px', fontWeight: 700, cursor: 'pointer',
              }}>{showAddForm ? '✕ Cancel' : '+ Add'}</button>
            </div>
            {/* Search */}
            <div style={{ padding: '6px 14px' }}>
              <input value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
                placeholder="Search questions..." style={{ width: '100%', padding: '5px 10px', border: '1px solid #eee', borderRadius: '6px', fontSize: '11px', boxSizing: 'border-box' }}
                aria-label="Search custom questions" />
            </div>
            {/* Add form */}
            {showAddForm && (
              <div style={{ padding: '8px 14px', borderBottom: '1px solid #f0f0f0', background: '#fff8f0' }}>
                <input value={newLabel} onChange={e => setNewLabel(e.target.value)} placeholder="Label (optional)"
                  style={{ width: '100%', padding: '5px 8px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '11px', marginBottom: '4px', boxSizing: 'border-box' }} aria-label="Label" />
                <textarea value={newQuestion} onChange={e => setNewQuestion(e.target.value)} placeholder="Your BI question..."
                  rows={2} style={{ width: '100%', padding: '5px 8px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '11px', marginBottom: '4px', boxSizing: 'border-box', resize: 'vertical' }} aria-label="Question" />
                <textarea value={newInstruction} onChange={e => setNewInstruction(e.target.value)}
                  placeholder="Instructions (optional): SQL or NLP, e.g. 'Search in tables orders, payments for answers' or 'SELECT SUM(total_amount) FROM orders WHERE...'"
                  rows={2} style={{ width: '100%', padding: '5px 8px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '11px', marginBottom: '4px', boxSizing: 'border-box', resize: 'vertical', fontFamily: 'monospace' }} aria-label="Instructions" />
                <button onClick={handleAdd} disabled={!newQuestion.trim()} style={{
                  padding: '4px 12px', background: newQuestion.trim() ? '#4CAF50' : '#ccc', color: '#fff',
                  border: 'none', borderRadius: '6px', fontSize: '11px', fontWeight: 700, cursor: newQuestion.trim() ? 'pointer' : 'not-allowed',
                }}>Save</button>
              </div>
            )}
            {/* ⚛️ Atomic Intelligence — questions grouped by topic */}
            {(filteredCustom.length > 0 || true) && (
              <div style={{ padding: '4px 0' }}>
                <div style={{ padding: '3px 14px', fontSize: '10px', fontWeight: 700, color: '#888', textTransform: 'uppercase' }}>⚛️ Atomic Intelligence</div>
                {/* Unassigned questions */}
                {(() => {
                  const unassigned = filteredCustom.filter(q => !q.atomic_topic);
                  if (unassigned.length > 0) return (
                    <div style={{ borderBottom: '1px solid #f0f0f0' }}>
                      <div style={{ padding: '4px 14px', fontSize: '10px', fontWeight: 600, color: '#999', background: '#fafafa' }}>📥 Unassigned</div>
                      {unassigned.map(q => (
                        <div key={q.question_id} style={rowStyle}>
                          <button onClick={() => handleToggleFav(q.question_id)} title={q.is_favorite ? 'Unfavorite' : 'Favorite'} style={iconBtn}>{q.is_favorite ? '⭐' : '☆'}</button>
                          <button onClick={() => handleRun(q)} style={{ flex: 1, background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', fontSize: '11px', color: '#333', padding: '2px 0' }}>
                            <div style={{ fontWeight: 500 }}>{q.label || q.question.slice(0, 60)}</div>
                            {q.instruction && <div style={{ fontSize: '9px', color: '#1565C0', fontFamily: 'monospace' }}>📝 {q.instruction.slice(0, 60)}...</div>}
                          </button>
                          {Object.entries(ATOMIC_TOPICS).map(([tk, tc]) => (
                            <button key={tk} onClick={() => handleAssignTopic(q.question_id, tk)} title={`Move to ${tc.label}`} style={{ ...iconBtn, fontSize: '10px' }}>{tc.icon}</button>
                          ))}
                          {/* DDLS: Deep-Dive LLM Session */}
                          <button onClick={() => {
                            setDdlsConsentPending({ question: q.question, provider: null });
                          }} title="Take me to Separate LLM Session (DDLS)" style={{ ...iconBtn, color: '#1565C0', fontSize: '13px' }}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#1565c0" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: 'middle' }}>
                              <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                              <polyline points="15 3 21 3 21 9" />
                              <line x1="10" y1="14" x2="21" y2="3" />
                            </svg>
                          </button>
                          {/* DDLS-2: Deep-Dive via alternate LLM */}
                          <button onClick={() => {
                            const altProvider = activeLlmProviders.find(p => p.id !== llmProvider);
                            setDdlsConsentPending({ question: q.question, provider: altProvider?.id || llmProvider, isDdls2: true });
                          }} title="Take me to Separate LLM Session (DDLS-2)" style={{ ...iconBtn, fontSize: '13px' }}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#00897b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: 'middle' }}>
                              <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                              <polyline points="15 3 21 3 21 9" />
                              <line x1="10" y1="14" x2="21" y2="3" />
                            </svg>
                          </button>
                          <button onClick={() => { setEditingId(q.question_id); setEditQuestion(q.question); setEditLabel(q.label || ''); setEditInstruction(q.instruction || ''); }} title="Edit" style={iconBtn}>✏️</button>
                          <button onClick={() => handleDelete(q.question_id)} title="Delete" style={{ ...iconBtn, color: '#ccc' }}>🗑</button>
                        </div>
                      ))}
                    </div>
                  );
                  return null;
                })()}
                {/* Topic groups: Recommend, Execute, Monitor Outcome, Decision To be Made */}
                {Object.entries(ATOMIC_TOPICS).map(([tKey, tCfg]) => {
                  const topicQs = filteredCustom.filter(q => q.atomic_topic === tKey);
                  return (
                    <div key={tKey} style={{ borderBottom: '1px solid #f0f0f0' }}>
                      <div style={{ padding: '4px 14px', fontSize: '10px', fontWeight: 600, color: '#283593', background: '#f8f9ff', display: 'flex', alignItems: 'center', gap: '4px' }}>
                        {tCfg.icon} {tCfg.label}
                        <span style={{ fontSize: '9px', color: '#999', fontWeight: 400, marginLeft: 'auto' }}>{topicQs.length}</span>
                      </div>
                      {topicQs.length === 0 ? (
                        <div style={{ padding: '4px 14px 4px 28px', fontSize: '10px', color: '#ccc' }}>No questions assigned</div>
                      ) : topicQs.map(q => (
                        <div key={q.question_id}>
                          {editingId === q.question_id ? (
                            <div style={{ padding: '6px 14px', background: '#f5f5f5' }}>
                              <input value={editLabel} onChange={e => setEditLabel(e.target.value)} placeholder="Label"
                                style={{ width: '100%', padding: '4px 8px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '11px', marginBottom: '3px', boxSizing: 'border-box' }} />
                              <textarea value={editQuestion} onChange={e => setEditQuestion(e.target.value)} rows={2}
                                style={{ width: '100%', padding: '4px 8px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '11px', marginBottom: '3px', boxSizing: 'border-box' }} />
                              <textarea value={editInstruction} onChange={e => setEditInstruction(e.target.value)} placeholder="Instructions (SQL/NLP)" rows={2}
                                style={{ width: '100%', padding: '4px 8px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '10px', marginBottom: '3px', boxSizing: 'border-box', fontFamily: 'monospace' }} />
                              <button onClick={() => handleUpdate(q.question_id)} style={{ ...iconBtn, color: '#4CAF50', fontSize: '11px', fontWeight: 700 }}>✓ Save</button>
                              <button onClick={() => setEditingId(null)} style={{ ...iconBtn, color: '#999', fontSize: '11px', marginLeft: '6px' }}>Cancel</button>
                            </div>
                          ) : (
                            <div style={{ ...rowStyle, paddingLeft: '28px' }}>
                              <button onClick={() => handleToggleFav(q.question_id)} title={q.is_favorite ? 'Unfavorite' : 'Favorite'} style={iconBtn}>{q.is_favorite ? '⭐' : '☆'}</button>
                              <button onClick={() => handleRun(q)} style={{ flex: 1, background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', fontSize: '11px', color: '#333', padding: '2px 0' }}>
                                <div style={{ fontWeight: 500 }}>{q.label || q.question.slice(0, 60)}</div>
                                {q.label && <div style={{ fontSize: '10px', color: '#888' }}>{q.question.slice(0, 80)}</div>}
                                {q.instruction && <div style={{ fontSize: '9px', color: '#1565C0', fontFamily: 'monospace' }}>📝 {q.instruction.slice(0, 60)}...</div>}
                              </button>
                              <span style={{ fontSize: '9px', color: '#aaa', minWidth: '18px' }}>{q.use_count}×</span>
                              {/* Move to other topics */}
                              {Object.entries(ATOMIC_TOPICS).filter(([k]) => k !== tKey).map(([k, t]) => (
                                <button key={k} onClick={() => handleAssignTopic(q.question_id, k)} title={`Move to ${t.label}`} style={{ ...iconBtn, fontSize: '10px' }}>{t.icon}</button>
                              ))}
                              <button onClick={() => handleAssignTopic(q.question_id, null)} title="Unassign" style={{ ...iconBtn, fontSize: '10px', color: '#999' }}>↩</button>
                              {/* DDLS: Deep-Dive LLM Session */}
                              <button onClick={() => {
                                setDeepDiveQuestion(q.question);
                              }} title="Take me to Separate LLM Session (DDLS)" style={{ ...iconBtn, color: '#1565C0', fontSize: '13px' }}>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#1565c0" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: 'middle' }}>
                                  <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                                  <polyline points="15 3 21 3 21 9" />
                                  <line x1="10" y1="14" x2="21" y2="3" />
                                </svg>
                              </button>
                              {/* DDLS-2: Deep-Dive via alternate LLM */}
                              <button onClick={() => {
                                const altProvider = activeLlmProviders.find(p => p.id !== llmProvider);
                                setDeepDiveQuestion({ question: q.question, provider: altProvider?.id || llmProvider, isDdls2: true });
                              }} title="Take me to Separate LLM Session (DDLS-2)" style={{ ...iconBtn, fontSize: '13px' }}>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#00897b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: 'middle' }}>
                                  <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                                  <polyline points="15 3 21 3 21 9" />
                                  <line x1="10" y1="14" x2="21" y2="3" />
                                </svg>
                              </button>
                              <button onClick={() => { setEditingId(q.question_id); setEditQuestion(q.question); setEditLabel(q.label || ''); setEditInstruction(q.instruction || ''); }} title="Edit" style={iconBtn}>✏️</button>
                              {!q.is_shared ? (
                                shareConsentId === q.question_id ? (
                                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', background: '#fff3e0', border: '1px solid #ffcc80', borderRadius: '6px', padding: '2px 6px', fontSize: '9px', whiteSpace: 'nowrap' }}>
                                    <span>Share?</span>
                                    <button onClick={() => handleShare(q.question_id)} style={{ ...iconBtn, color: '#4CAF50', fontSize: '11px', fontWeight: 700 }} title="Confirm share">✓</button>
                                    <button onClick={() => setShareConsentId(null)} style={{ ...iconBtn, color: '#999', fontSize: '11px' }} title="Cancel">✕</button>
                                  </span>
                                ) : (
                                  <button onClick={() => setShareConsentId(q.question_id)} title="Share question" style={{ ...iconBtn, position: 'relative' }}>
                                    <span style={{ fontSize: '13px', opacity: 0.6 }}>📤</span>
                                    <span style={{ position: 'absolute', top: '-2px', right: '-4px', fontSize: '9px', lineHeight: 1 }}>❌</span>
                                  </button>
                                )
                              ) : (
                                <button onClick={() => handleUnshare(q.question_id)} title="Unshare" style={iconBtn}>
                                  <span style={{ fontSize: '11px' }}>✅</span>
                                </button>
                              )}
                              <button onClick={() => handleDelete(q.question_id)} title="Delete" style={{ ...iconBtn, color: '#ccc' }}>🗑</button>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
            )}
            {/* Shared — only question text is visible, never instructions/SQL */}
            {filteredShared.length > 0 && (
              <div style={{ padding: '4px 0', borderTop: '1px solid #e8e8e8' }}>
                <div style={{ padding: '3px 14px', fontSize: '10px', fontWeight: 700, color: '#888', textTransform: 'uppercase' }}>Shared by Similar Businesses (Questions Only)</div>
                {filteredShared.map(q => (
                  <div key={q.question_id} style={rowStyle}>
                    <span style={{ fontSize: '13px' }}>🌐</span>
                    <button onClick={() => { onRunQuestion(q.question); }} style={{ flex: 1, background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', fontSize: '11px', color: '#333', padding: '2px 0' }}>
                      <div>{q.question.slice(0, 80)}</div>
                      <div style={{ fontSize: '10px', color: '#1565C0', fontStyle: 'italic' }} title={q.shared_by_name}>— {q.shared_by_name}</div>
                    </button>
                    <span style={{ fontSize: '9px', color: '#aaa' }}>{q.use_count}×</span>
                  </div>
                ))}
              </div>
            )}
            {filteredCustom.length === 0 && filteredShared.length === 0 && !showAddForm && (
              <div style={{ padding: '16px', textAlign: 'center', color: '#999', fontSize: '12px' }}>No questions found. Click "+ Add" to create one.</div>
            )}
          </div>
        )}
      </div>

      {/* LLM Suggestions Dropdown */}
      <div style={{ position: 'relative', display: 'inline-block' }}>
        <button
          ref={aiTop10ChipRef}
          onClick={() => { setShowLLM(!showLLM); setShowSummary(false); setShowSharedBI(false); loadLlmQuestions(); }}
          onMouseEnter={() => setCidTooltipVisible(true)}
          onMouseLeave={() => setCidTooltipVisible(false)}
          title={`🧠 AI Top-10 BI Questions for product type=${productDisplayName}`}
          style={chipStyle('#e8eaf6', '#9fa8da', '#283593')}>
          🤖 AI Top-10 ▾
        </button>
        <CIDTooltip
          clientId={clientId}
          visible={cidTooltipVisible}
          onDismiss={() => setCidTooltipVisible(false)}
          anchorRef={aiTop10ChipRef}
        />
        {showLLM && (
          <div style={dd}>
            <div style={{ padding: '10px 14px', borderBottom: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontWeight: 700, fontSize: '13px' }}>Product Type: {productDisplayName}</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <select value={llmProvider} onChange={e => { if (onProviderChange) onProviderChange(e.target.value); }}
                  style={{ padding: '3px 8px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '11px' }}
                  aria-label="Select LLM provider">
                  {activeLlmProviders.map(p => (
                    <option key={p.id} value={p.id}>{p.icon} {p.label}</option>
                  ))}
                </select>
                <button onClick={loadLlmQuestions} title="Refresh questions for this LLM" style={{
                  background: llmRefreshing ? '#e3f2fd' : '#fff', border: '1px solid #ddd', borderRadius: '6px', cursor: 'pointer',
                  padding: '3px 8px', fontSize: '12px', lineHeight: 1, fontWeight: 600, color: '#1565C0',
                  transition: 'all 0.2s',
                }}>{llmRefreshing ? '⏳' : '🔄'} Refresh</button>
              </div>
            </div>
            <div style={{ padding: '4px 14px', fontSize: '10px', color: '#888' }}>
              Top questions for {productDisplayName} via {activeLlmProviders.find(p => p.id === llmProvider)?.label}. Grouped by Atomic topic. Click ➕ to adopt.
            </div>
            {llmRefreshing ? (
              <div style={{ padding: '20px', textAlign: 'center', color: '#1565C0', fontSize: '13px' }}>
                ⏳ Refreshing questions for {activeLlmProviders.find(p => p.id === llmProvider)?.label}...
              </div>
            ) : (llmQuestions || []).length === 0 ? (
              <div style={{ padding: '16px', textAlign: 'center', color: '#999', fontSize: '12px' }}>No industry questions available for this product type.</div>
            ) : (() => {
              /* Auto-classify LLM questions into Atomic topics by keyword signals */
              const qs = llmQuestions || [];
              const TOPIC_SIGNALS = {
                recommend: ['which', 'best', 'highest', 'lowest', 'top', 'should', 'suggest', 'optimize', 'most', 'profit', 'margin', 'opportunity'],
                execute: ['what is', 'calculate', 'total', 'count', 'sum', 'average', 'current', 'today', 'percentage', 'rate', 'cost', 'revenue'],
                monitor: ['how does', 'compare', 'trend', 'over time', 'vs', 'impact', 'affect', 'change', 'week', 'month', 'quarter', 'seasonal'],
                decision: ['at risk', 'overdue', 'within', 'approaching', 'decline', 'churn', 'renewal', 'threshold', 'convert', 'unmet', 'demand'],
              };
              const topicSlices = { recommend: [], execute: [], monitor: [], decision: [] };
              const assigned = new Set();
              // Priority order: recommend → execute → monitor → decision
              const topicOrder = ['recommend', 'execute', 'monitor', 'decision'];
              for (const tKey of topicOrder) {
                const signals = TOPIC_SIGNALS[tKey];
                for (const q of qs) {
                  if (assigned.has(q)) continue;
                  const lower = q.toLowerCase();
                  if (signals.some(s => lower.includes(s))) {
                    topicSlices[tKey].push(q);
                    assigned.add(q);
                  }
                }
              }
              // Unclassified questions go to the first topic with fewest items (balanced distribution)
              for (const q of qs) {
                if (!assigned.has(q)) {
                  const smallest = topicOrder.reduce((a, b) => topicSlices[a].length <= topicSlices[b].length ? a : b);
                  topicSlices[smallest].push(q);
                }
              }
              return Object.entries(ATOMIC_TOPICS).map(([tKey, tCfg]) => (
                <div key={tKey} style={{ borderBottom: '1px solid #f0f0f0' }}>
                  <div style={{ padding: '6px 14px', background: '#f8f9ff', fontSize: '11px', fontWeight: 700, color: '#283593', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    {tCfg.icon} {tCfg.label}
                    <span style={{ fontSize: '9px', color: '#999', fontWeight: 400, marginLeft: 'auto' }}>{(topicSlices[tKey] || []).length} questions</span>
                  </div>
                  {(topicSlices[tKey] || []).map((q, i) => (
                    <div key={i} style={rowStyle}>
                      <span style={{ fontSize: '11px', color: '#aaa', minWidth: '18px' }}>{i + 1}.</span>
                      <button onClick={() => { onRunQuestion(q); }} style={{ flex: 1, background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', fontSize: '12px', color: '#333', padding: '2px 0' }}>
                        {q}
                      </button>
                      <button onClick={() => {
                        /* Adopt to matching Atomic topic */
                        const entry = {
                          question_id: `cq-${Date.now()}-${i}`, question: q, label: q.slice(0, 60),
                          instruction: '', category: 'llm_suggested', is_favorite: false,
                          use_count: 0, is_shared: false, share_approved: false,
                          shared_by_name: companyName, atomic_topic: tKey,
                          created_at: new Date().toISOString(),
                        };
                        saveToLocal([entry, ...customQuestions]);
                        setLlmQuestions(prev => {
                          if (!prev) return prev;
                          const remaining = prev.filter(x => x !== q);
                          const keyMap = { wellness: 'medical', auto: 'valet', freight: 'logistics', 'real estate': 'realty', bank: 'banking', financial: 'banking', supermarket: 'grocery', 'china-us': 'geopolitical', 'strategic-intel': 'geopolitical', 'geopulse': 'geopolitical' };
                          const resolvedKey = keyMap[bkey] || bkey;
                          const allPool = getQuestionsForProvider(llmProvider, resolvedKey);
                          const ciSet = new Set([...customQuestions.map(x => x.question.toLowerCase().trim()), q.toLowerCase().trim()]);
                          const remainingSet = new Set(remaining.map(x => x.toLowerCase().trim()));
                          const backfill = allPool.filter(x => !ciSet.has(x.toLowerCase().trim()) && !remainingSet.has(x.toLowerCase().trim()));
                          if (backfill.length > 0 && remaining.length < 10) remaining.push(backfill[0]);
                          return remaining.slice(0, 10);
                        });
                      }} title={`Add to ${tCfg.label} in Atomic Intelligence`} style={{ ...iconBtn, color: '#4CAF50' }}>➕</button>
                      <button onClick={() => {
                        /* Share with consent */
                        if (window.confirm(`Share this question?\n\n"${q}"\n\nThis will make it visible to other users in your organization.`)) {
                          handleStageForAtomic(q);
                        }
                      }} title="Share this question" style={{ ...iconBtn, color: '#1565C0', fontSize: '12px' }}>📤</button>
                      <button onClick={() => {
                        /* Take me to separate LLM session — deep-dive (DDLS) */
                        if (window.confirm(`Start a Deep-Dive LLM Session?\n\n"${q}"\n\nThis will open a separate session with in-depth analysis, reasons, solutions, and actionable recommendations.`)) {
                          setDeepDiveQuestion(q);
                        }
                      }} title="Take me to separate LLM session (DDLS)" style={{ ...iconBtn, color: '#1565C0', fontSize: '13px' }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#1565c0" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: 'middle' }}>
                          <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                          <polyline points="15 3 21 3 21 9" />
                          <line x1="10" y1="14" x2="21" y2="3" />
                        </svg>
                      </button>
                      {/* DDLS-2: Deep-dive session for alternate LLM provider (configurable color) */}
                      <button onClick={() => {
                        const altProvider = activeLlmProviders.find(p => p.id !== llmProvider);
                        const altLabel = altProvider ? altProvider.label : 'alternate LLM';
                        if (window.confirm(`Start a Deep-Dive Session via ${altLabel}?\n\n"${q}"\n\nCompare analysis from a different LLM provider.`)) {
                          setDeepDiveQuestion({ question: q, provider: altProvider?.id || llmProvider, isDdls2: true });
                        }
                      }} title={`Deep-Dive via alternate LLM (DDLS-2)`} style={{ ...iconBtn, fontSize: '13px' }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#00897b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: 'middle' }}>
                          <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                          <polyline points="15 3 21 3 21 9" />
                          <line x1="10" y1="14" x2="21" y2="3" />
                        </svg>
                      </button>
                      {/* Agentic-AI button — replaces "Run this question →" */}
                      {/* Default: runs the question. Upgrades to multi-step orchestration when DDLS-2 session is active */}
                      <span
                        onClick={() => onRunQuestion(q)}
                        title="Let AI agents autonomously orchestrate all required actions for this business use case — from data retrieval to insight delivery"
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => e.key === 'Enter' && onRunQuestion(q)}
                        style={{
                          fontSize: '15px', cursor: 'pointer', padding: '0 2px',
                          display: 'inline-flex', alignItems: 'center',
                          filter: 'drop-shadow(0 0 2px rgba(0,137,123,0.4))',
                          transition: 'filter 0.2s',
                        }}
                        onMouseEnter={e => e.currentTarget.style.filter = 'drop-shadow(0 0 5px rgba(0,137,123,0.8))'}
                        onMouseLeave={e => e.currentTarget.style.filter = 'drop-shadow(0 0 2px rgba(0,137,123,0.4))'}
                      >
                        🐙
                      </span>
                    </div>
                  ))}
                </div>
              ));
            })()}
          </div>
        )}
      </div>

      {/* Shared BI Dropdown — questions from outside the organization, grouped by company */}
      <div style={{ position: 'relative', display: 'inline-block' }}>
        <button onClick={() => { setShowLLM(false); setShowSummary(false); setShowCustomOpen(false); setShowSharedBI(p => !p); }}
          style={chipStyle('#e0f2f1', '#80cbc4', '#004D40')}>
          🌐 Shared BI ▾
        </button>
        {showSharedBI && (
          <div style={dd}>
            <div style={{ padding: '10px 14px', borderBottom: '1px solid #f0f0f0' }}>
              <span style={{ fontWeight: 700, fontSize: '13px' }}>🌐 Shared BI Questions</span>
              <div style={{ fontSize: '10px', color: '#888', marginTop: '2px' }}>Questions shared by other organizations (questions only — no solutions)</div>
            </div>
            {sharedBIGrouped.length === 0 ? (
              <div style={{ padding: '16px', textAlign: 'center', color: '#999', fontSize: '12px' }}>
                No shared BI questions available. Other organizations in your business type have not shared questions yet.
              </div>
            ) : (
              sharedBIGrouped.map(group => (
                <div key={group.company} style={{ borderBottom: '1px solid #f5f5f5' }}>
                  <div
                    style={{ padding: '8px 14px', background: '#f5f5f5', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', fontWeight: 600 }}
                    onClick={() => setExpandedSharedCompany(prev => prev === group.company ? null : group.company)}
                    title={group.company}
                  >
                    <span>{expandedSharedCompany === group.company ? '▼' : '▶'}</span>
                    <span style={{ maxWidth: '140px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={group.company}>
                      {group.companyShort}
                    </span>
                    <span style={{ fontSize: '10px', color: '#888', marginLeft: 'auto' }}>{group.questions.length} question{group.questions.length !== 1 ? 's' : ''}</span>
                  </div>
                  {expandedSharedCompany === group.company && (
                    <div>
                      {group.questions.map((q, i) => (
                        <div key={q.question_id || i} style={{ ...rowStyle, paddingLeft: '28px' }}>
                          <span style={{ fontSize: '11px', color: '#aaa', minWidth: '14px' }}>{i + 1}.</span>
                          <button onClick={() => onRunQuestion(q.question)} style={{ flex: 1, background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', fontSize: '11px', color: '#333', padding: '2px 0' }}>
                            {q.question.slice(0, 90)}{q.question.length > 90 ? '…' : ''}
                          </button>
                          <span style={{ fontSize: '9px', color: '#aaa' }}>{q.use_count || 0}×</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Self-Learning Report (includes patterns + use cases + export) */}
      <SelfLearningUseCases clientId={clientId} chipStyle={chipStyle} dd={dd} onRunQuestion={onRunQuestion} rowStyle={rowStyle} iconBtn={iconBtn} />

      {/* Summarized Reports Button */}
      <button onClick={handleSummary} style={chipStyle('#e8f5e9', '#a5d6a7', '#1B5E20')}>
        📊 Summarized Reports
      </button>

      {/* Summary Modal */}
      {showSummary && summaryResult && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => setShowSummary(false)}>
          <div style={{ background: '#fff', borderRadius: '12px', padding: '24px', maxWidth: '700px', width: '90%', maxHeight: '80vh', overflowY: 'auto' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ margin: 0 }}>📊 Summarized Intelligence Report</h3>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button onClick={() => {
                  const printContent = document.getElementById('summary-report-content');
                  if (!printContent) return;
                  const win = window.open('', '_blank');
                  win.document.write(`<!DOCTYPE html><html><head><title>Intelligence Report - ${companyName}</title>
                    <style>body{font-family:Calibri,Arial,sans-serif;padding:24px;color:#333;font-size:13px}
                    h1{font-size:18px;color:#1a1a2e;margin-bottom:4px}
                    .meta{color:#888;font-size:11px;margin-bottom:16px}
                    .item{padding:10px;margin-bottom:4px;border-radius:4px}
                    .item:nth-child(even){background:#f5f5f5}
                    .query{font-weight:600;color:#1565C0;margin-bottom:3px;font-size:12px}
                    .answer{color:#555;white-space:pre-wrap;font-size:12px}
                    .footer{margin-top:20px;padding-top:10px;border-top:1px solid #ddd;font-size:10px;color:#aaa}
                    @media print{body{padding:12px}}</style></head><body>`);
                  win.document.write(`<h1>📊 Summarized Intelligence Report</h1>`);
                  win.document.write(`<div style="font-size:14px;font-weight:600;margin-bottom:2px">${companyName}</div>`);
                  win.document.write(`<div class="meta">Generated: ${new Date(summaryResult.generated_at).toLocaleString()} • ${summaryResult.operations_count} operations + ${summaryResult.custom_count} custom queries</div>`);
                  summaryResult.results.forEach((r, i) => {
                    win.document.write(`<div class="item"><div class="query">${i + 1}. ${r.query}</div><div class="answer">${r.answer}</div></div>`);
                  });
                  win.document.write(`<div class="footer">Platform: plug-n-play-infra.ai • Provider: Empirical-AIs • ${new Date().toLocaleDateString()}</div>`);
                  win.document.write('</body></html>');
                  win.document.close();
                  setTimeout(() => { win.print(); }, 300);
                }} style={{
                  padding: '5px 12px', background: '#1565C0', color: '#fff', border: 'none',
                  borderRadius: '6px', fontSize: '11px', fontWeight: 600, cursor: 'pointer',
                }}>📥 Save as PDF</button>
                <button onClick={() => setShowSummary(false)} style={{ background: 'none', border: 'none', fontSize: '18px', cursor: 'pointer' }}>✕</button>
              </div>
            </div>
            <div id="summary-report-content">
              <div style={{ fontSize: '12px', color: '#888', marginBottom: '12px' }}>
                Generated: {new Date(summaryResult.generated_at).toLocaleString()} •
                {summaryResult.operations_count} operations + {summaryResult.custom_count} custom queries
              </div>
              {summaryResult.results.map((r, i) => (
                <div key={i} style={{ padding: '10px', background: i % 2 === 0 ? '#f9f9f9' : '#fff', borderRadius: '6px', marginBottom: '4px' }}>
                  <div style={{ fontWeight: 600, fontSize: '12px', color: '#1565C0', marginBottom: '4px' }}>{i + 1}. {r.query}...</div>
                  <div style={{ fontSize: '12px', color: '#555', whiteSpace: 'pre-wrap' }}>{r.answer}...</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      {summaryLoading && <span style={{ fontSize: '12px', color: '#888' }}>Generating summary...</span>}

      {/* DDLS Consent Modal */}
      {ddlsConsentPending && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.4)', zIndex: 10000,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <div style={{
            background: '#fff', borderRadius: '12px', padding: '24px', maxWidth: '420px', width: '90%',
            boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
          }}>
            <div style={{ fontSize: '15px', fontWeight: 600, marginBottom: '12px', color: '#333' }}>
              🔍 Start Deep-Dive LLM Session?
            </div>
            <div style={{
              fontSize: '12px', color: '#555', marginBottom: '12px', lineHeight: 1.6,
              background: '#f8f9ff', padding: '10px 12px', borderRadius: '8px', border: '1px solid #e3e8f0',
            }}>
              "{ddlsConsentPending.question && ddlsConsentPending.question.length > 120 ? ddlsConsentPending.question.slice(0, 120) + '...' : ddlsConsentPending.question}"
            </div>
            <div style={{ fontSize: '11px', color: '#888', marginBottom: '16px', lineHeight: 1.5 }}>
              This will open a separate LLM session that provides in-depth analysis, root causes, solutions, and actionable recommendations for this business question. Your question will be sent to the AI for detailed processing.
            </div>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button onClick={() => setDdlsConsentPending(null)} style={{
                padding: '8px 16px', borderRadius: '6px', border: '1px solid #ddd',
                background: '#fff', color: '#666', cursor: 'pointer', fontSize: '12px',
              }}>Cancel</button>
              <button onClick={() => {
                const pending = ddlsConsentPending;
                setDdlsConsentPending(null);
                if (pending.provider) {
                  setDeepDiveQuestion({ question: pending.question, provider: pending.provider, isDdls2: pending.isDdls2 });
                } else {
                  setDeepDiveQuestion(pending.question);
                }
              }} style={{
                padding: '8px 16px', borderRadius: '6px', border: 'none',
                background: '#1565c0', color: '#fff', cursor: 'pointer', fontSize: '12px', fontWeight: 600,
              }}>🔍 Start Session</button>
            </div>
          </div>
        </div>
      )}

      {/* Deep-Dive LLM Session Modal — triggered from AI Top-10 menu */}
      {deepDiveQuestion && (
        <DeepDiveModal
          question={typeof deepDiveQuestion === 'object' ? deepDiveQuestion.question : deepDiveQuestion}
          actionType="Recommend"
          clientId={clientId}
          productType={productType}
          llmProvider={typeof deepDiveQuestion === 'object' && deepDiveQuestion.provider ? deepDiveQuestion.provider : llmProvider}
          onClose={() => setDeepDiveQuestion(null)}
        />
      )}

    </>
  );
};

export default CustomIntelligencePanel;
export { SelfLearningBadge, SelfLearningUseCases };
